import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { ticketsApi } from './api';
import { deriveDiscoveryFlags, shouldRebroadcastBus } from './discovery-convergence';
import { queryKeys } from './query-keys';
import { useBeckonDraft } from './use-beckon-draft';

/**
 * Polls the metro booking draft for a transaction — a thin wrapper over the
 * generic useBeckonDraft (shared with rides). Stops when the current step
 * completes/errors; flags timedOut after ~30s.
 */
export function useTransitDraft(transactionId: string | null) {
  return useBeckonDraft(transactionId, ticketsApi.draft, queryKeys.draft);
}

/**
 * Catalog-driven station discovery for a city. On a server cache hit the station
 * list comes back inline ("ready"); on a miss the backend triggers a city-only
 * search1 and we poll the resulting draft until the BPP's catalog (stations)
 * arrives. The seed DB is no longer involved — stations carry the BPP's own codes.
 */
export function useStationDiscovery(
  cityCode: string | null,
  vehicleCategory: 'METRO' | 'BUS',
  // Chosen operator's bpp_id (from the operator picker). Empty for a single-operator city — the
  // backend then uses broadcast discovery. When set, discovery routes directly to that BPP and the
  // catalog is cached per operator, so two operators in one city (Mumbai) never clobber each other.
  operatorBppId = ''
) {
  // One-shot flag: when set, the next discovery fetch forces a fresh broadcast (bypasses the cached
  // BPP) so a degraded seller is re-picked to a healthy one. Reset immediately so normal refetches
  // (e.g. window refocus) keep using the cache.
  const forceRefresh = useRef(false);
  const discoverQuery = useQuery({
    queryKey: queryKeys.stations(cityCode ?? '', vehicleCategory, operatorBppId),
    queryFn: () => {
      const refresh = forceRefresh.current;
      forceRefresh.current = false;
      return ticketsApi.stations(cityCode as string, vehicleCategory, operatorBppId, refresh);
    },
    enabled: Boolean(cityCode),
    staleTime: 5 * 60_000, // catalog is cached server-side; avoid refetch storms
  });

  const txnId =
    discoverQuery.data?.status === 'discovering'
      ? (discoverQuery.data.transactionId ?? null)
      : null;
  const draft = useTransitDraft(txnId);

  const isReady = discoverQuery.data?.status === 'ready';
  const stations = isReady ? (discoverQuery.data?.stations ?? []) : (draft.draft?.stations ?? []);

  // The BPP that served the catalog. The follow-up fare search must route directly to
  // it (otherwise it broadcasts and the fare options carry no bpp_uri, breaking select).
  // A cache hit ("ready") carries it inline; a miss surfaces it on the polled draft.
  const bppId = (isReady ? discoverQuery.data?.bppId : draft.draft?.bppId) ?? '';
  const bppUri = (isReady ? discoverQuery.data?.bppUri : draft.draft?.bppUri) ?? '';

  // Force a FRESH broadcast discovery, bypassing the cached BPP (a degraded seller is re-picked). Used by
  // the manual "try another operator" action and by the bounded bus re-broadcast below.
  const retryDiscovery = () => {
    forceRefresh.current = true;
    return discoverQuery.refetch();
  };

  // The backend flips a city discovery to "unavailable" once its TTL elapses with no serving BPP — the
  // honest "no operators" terminal — and also immediately when a BUS seller answers with a non-bookable
  // "fare menu" instead of a station list.
  const rawUnavailable = draft.draft?.status === 'unavailable';

  // Bus: converge on the station list by re-broadcasting a bounded number of times before surfacing a real
  // "no operators". One retry per distinct discovery txn (guarded by lastRetriedTxn) so we never loop
  // within a single draft. Metro never retries — its "unavailable" is terminal.
  const [busRetries, setBusRetries] = useState(0);
  const lastRetriedTxn = useRef<string | null>(null);
  useEffect(() => {
    setBusRetries(0);
    lastRetriedTxn.current = null;
  }, [cityCode, vehicleCategory, operatorBppId]);
  useEffect(() => {
    if (
      shouldRebroadcastBus({
        vehicleCategory,
        rawUnavailable,
        stationsLen: stations.length,
        busRetries,
        txnId,
        lastRetriedTxn: lastRetriedTxn.current,
      })
    ) {
      lastRetriedTxn.current = txnId;
      setBusRetries((n) => n + 1);
      void retryDiscovery();
    }
    // retryDiscovery closes over stable refs + the query handle; intentionally omitted from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleCategory, rawUnavailable, stations.length, busRetries, txnId]);

  // While a bus discovery still has retry budget, stay in "discovering" (not "no operators").
  const { busRetrying, unavailable } = deriveDiscoveryFlags({
    vehicleCategory,
    rawUnavailable,
    busRetries,
  });

  const isLoading =
    discoverQuery.isLoading ||
    busRetrying ||
    (Boolean(txnId) &&
      stations.length === 0 &&
      !unavailable &&
      !draft.timedOut &&
      draft.draft?.status !== 'error');

  return {
    stations,
    bppId,
    bppUri,
    // True when discovery settled with no serving operator for the city (TTL elapsed / all responders
    // dropped). The UI shows an honest "no operators available" empty state rather than a spinner.
    unavailable,
    // The search1 (city discovery) transaction, when discovery did a live broadcast
    // (miss). The booking/fare search reuses it so the catalog→station search pair
    // shares one transaction_id (ONDC TRV11 2-step search). Null on a cache hit.
    discoveryTransactionId: txnId,
    isLoading,
    error: discoverQuery.error ?? draft.error,
    timedOut: draft.timedOut,
    // Re-run discovery (cache hit → ready; miss → fresh search1) — used to recover
    // from a discovery timeout without forcing the user to switch cities.
    refetch: discoverQuery.refetch,
    // Force a FRESH broadcast discovery, bypassing the cached BPP — used by the "try another operator"
    // action. The caller must clear the chosen stations afterward (codes are BPP-specific).
    retryDiscovery,
  };
}
